import { useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  appliedJobsService,
  jobProfileService,
  profileService,
  subscriptionService,
  eventsService,
  authService,
  getJobQuestions,
} from "@/api/services";
import { useAuthStore } from "@/store";
import { useNavigate } from "react-router-dom";
import type { AppliedJobsQuery, JobQuestionsQuery } from "@/types";
import { getRewardfulId } from "@/utils/rewardful";

// ─── Query Keys ───────────────────────────────────────────────────────────────
export const QK = {
  profile: ["profile"] as const,
  jobProfile: ["job-profile"] as const,
  appliedJobs: (p?: AppliedJobsQuery) => ["applied-jobs", p] as const,
  jobQuestions: (p?: JobQuestionsQuery) => ["job-questions", p] as const,
  plans: ["plans"] as const,
  events: (p?: object) => ["events", p] as const,
};

// ─── Profile ──────────────────────────────────────────────────────────────────
export function useProfile() {
  const isAuth = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const qc = useQueryClient();
  const referralPersistAttempted = useRef(false);

  const query = useQuery({
    queryKey: QK.profile,
    queryFn: () => profileService.get().then((r) => r.data?.data),
    enabled: isAuth,
    staleTime: 2 * 60_000,
  });

  useEffect(() => {
    if (!isAuth) return;
    if (referralPersistAttempted.current) return;

    const profile = query.data as
      | { clientReference?: { rewardfulId?: string } }
      | undefined;

    if (!profile) return;
    if (profile.clientReference?.rewardfulId) return;

    const rewardfulId = getRewardfulId();
    if (!rewardfulId) return;

    referralPersistAttempted.current = true;

    profileService
      .update({
        clientReference: {
          ...(profile.clientReference ?? {}),
          rewardfulId,
        },
      })
      .then(() => {
        setUser({
          clientReference: {
            ...(profile.clientReference ?? {}),
            rewardfulId,
          },
        });
        qc.invalidateQueries({ queryKey: QK.profile });
        if (import.meta.env.DEV) {
          console.debug("[rewardful] persisted rewardfulId on profile");
        }
      })
      .catch(() => {
        referralPersistAttempted.current = false;
      });
  }, [isAuth, qc, query.data, setUser]);

  // Sync user data to extension storage when user is authenticated
  useEffect(() => {
    if (!isAuth || !user) return;

    import("@/lib/extensionStorage").then(({ syncSubscriptionToExtension }) => {
      console.log("[useProfile] Syncing user data to extension:", user);
      syncSubscriptionToExtension(user).catch((err) => {
        console.error("[useProfile] Failed to sync user data:", err);
      });
    });
  }, [isAuth, user]);

  return query;
}

// ─── Job Profile ──────────────────────────────────────────────────────────────
export function useJobProfile() {
  return useQuery({
    queryKey: QK.jobProfile,
    queryFn: () => jobProfileService.show().then((r) => r.data?.data),
    staleTime: 60_000,
  });
}

export function useUploadResume() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) =>
      jobProfileService.uploadResume(file).then((r) => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: QK.jobProfile });
      qc.refetchQueries({ queryKey: QK.jobProfile });
      toast.success("Resume uploaded!");
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || "Upload failed";
      toast.error(errorMessage);
    },
  });
}

export function useUpdateSkills() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (skills: string[]) =>
      jobProfileService.updateSkills({ skills }).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.jobProfile });
      toast.success("Skills updated");
    },
  });
}

export function useUpdateNegativeKeywords() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keywords: string[]) =>
      jobProfileService
        .updateNegativeKeywords({ keywords })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.jobProfile });
      toast.success("Keywords updated");
    },
  });
}

export function useParseJobCollection() {
  return useMutation({
    mutationFn: (jobDescription: string) =>
      jobProfileService
        .parseJobCollection({ jobDescription })
        .then((r) => r.data),
  });
}

export function useEnhanceResume() {
  return useMutation({
    mutationFn: (p: { jobDescriptionHash: string; templateId?: string }) =>
      jobProfileService.enhanceAndGeneratePdf(p).then((r) => r.data),
    onSuccess: () => toast.success("Tailored resume ready!"),
    onError: () => toast.error("Enhancement failed"),
  });
}

// ─── Applied Jobs ─────────────────────────────────────────────────────────────
export function useJobQuestions(params: JobQuestionsQuery) {
  const token = useAuthStore((s) => s.token);

  return useQuery({
    queryKey: QK.jobQuestions(params),
    enabled: Boolean(token),
    queryFn: ({ signal }) =>
      getJobQuestions({
        ...params,
        token: token ?? "",
        signal,
      }),
    staleTime: 30_000,
    placeholderData: (previousData) => previousData,
  });
}

export function useAppliedJobs(params?: AppliedJobsQuery) {
  return useQuery({
    queryKey: QK.appliedJobs(params),
    queryFn: () => appliedJobsService.getAll(params).then((r) => r.data),
    staleTime: 30_000,
  });
}

export function useUpdateAppliedJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Record<string, unknown>;
    }) => appliedJobsService.update(id, payload).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["applied-jobs"] });
      toast.success("Updated");
    },
    onError: () => toast.error("Failed to update"),
  });
}

// ─── Subscription ─────────────────────────────────────────────────────────────
export function usePlans() {
  return useQuery({
    queryKey: QK.plans,
    queryFn: async () => {
      const response = await subscriptionService.getPlans();
      const plans = response.data?.data || [];

      // Transform backend plans to frontend format
      return plans.map((plan: any) => ({
        _id: plan._id,
        name: plan.title,
        type: plan.type,
        price: plan.price,
        interval: plan.period?.toLowerCase() || "monthly",
        features: plan.details || [],
        jobsPerMonth: parseInt(plan.jobsAllowed) || 100,
      }));
    },
    staleTime: 5 * 60_000,
  });
}

export function useCreatePaymentSession() {
  const userRewardfulId = useAuthStore(
    (s) => s.user?.clientReference?.rewardfulId,
  );

  return useMutation({
    mutationFn: (planId: string) => {
      const clientReferenceId =
        userRewardfulId ?? getRewardfulId() ?? undefined;
      return subscriptionService
        .createPaymentSession({ planId, clientReferenceId })
        .then((r) => r.data);
    },
    onSuccess: (data) => {
      const url = data?.data?.data?.sessionUrl;
      if (url) {
        window.location.href = url;
      } else {
        toast.error("No checkout URL received");
      }
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Could not start checkout");
    },
  });
}

export function useCreateBillingSession() {
  return useMutation({
    mutationFn: () =>
      subscriptionService.createBillingSession().then((r) => r.data),
    onSuccess: (data) => {
      const url = data?.data?.data?.sessionUrl;
      if (url) {
        window.location.href = url;
      } else {
        toast.error("No billing portal URL received");
      }
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Could not open billing portal",
      );
    },
  });
}

// ─── Analytics ────────────────────────────────────────────────────────────────
export function useEventStats(params?: {
  dateRange?: string;
  platform?: string;
}) {
  return useQuery({
    queryKey: QK.events(params),
    queryFn: () => eventsService.getStats(params).then((r) => r.data),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    retry: 1,
    throwOnError: false,
  });
}

// ─── Auth ─────────────────────────────────────────────────────────────────────
export function useLogin() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (p: { email: string; password: string }) =>
      authService.login(p).then((r) => r.data),
    onSuccess: (data) => {
      const { user, token, refreshToken } = data.data;
      setAuth(user, token, refreshToken);
      navigate(user.isJobProfileCompleted ? "/dashboard" : "/onboarding");
    },
    onError: (err: unknown) =>
      toast.error((err as { message?: string })?.message ?? "Login failed"),
  });
}

export function useRegister() {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (p: { firstName: string; email: string; password: string }) =>
      authService.register(p).then((r) => r.data),
    onSuccess: (_data, vars) => {
      toast.success("Check your email for the OTP");
      navigate(`/verify-otp?email=${encodeURIComponent(vars.email)}`);
    },
    onError: (err: unknown) =>
      toast.error(
        (err as { message?: string })?.message ?? "Registration failed",
      ),
  });
}

export function useVerifyOtp() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  return useMutation({
    mutationFn: (p: { email: string; otp: string; isVerifyEmail?: boolean }) =>
      authService.verifyOtp(p).then((r) => r.data),
    onSuccess: (data) => {
      console.log("[useVerifyOtp] Success response:", data);
      const inner = data?.data;

      if (inner?.token && inner?.refreshToken && inner?.user) {
        setAuth(inner.user, inner.token, inner.refreshToken);
        toast.success("Email verified successfully!");
        navigate("/dashboard");
      } else {
        // Backend didn't return tokens (old flow) — redirect to login
        toast.success("Email verified! Please login to continue.");
        navigate("/login");
      }
    },
    onError: (error: any) => {
      console.error("[useVerifyOtp] Error:", error);
      toast.error(error?.response?.data?.message || "Invalid OTP");
    },
  });
}

export function useLogout() {
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const qc = useQueryClient();
  return () => {
    logout();
    qc.clear();
    navigate("/login");
    toast.success("Logged out");
  };
}
